import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useToast } from "@shared/ui/use-toast";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { axScheduleApi } from "./api";

export const useGetDashboardData = () => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.schedule.getDashboardData(remoteUtilityId),
    () => axScheduleApi.getDashboardData(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useGetAllSchedules = (
  page: number,
  limit: number,
  search?: string,
  status?: number,
  system_type?: string,
  criticality?: string
) => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.schedule.getAllSchedules(
      remoteUtilityId,
      page,
      limit,
      search,
      status,
      system_type,
      criticality
    ),
    () =>
      axScheduleApi.getAllSchedules(
        remoteUtilityId,
        page,
        limit,
        search,
        status,
        system_type,
        criticality
      ),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useGetChoices = (type: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.schedule.getChoices(type),
    () => axScheduleApi.getChoices(type),
    {
      enabled: true,
    }
  );
};

export const useGetAllSOPs = (
  searchData?: string,
  sopAssociationType?: string,
  operationType?: string,
  page?: number,
  limit?: number
) => {
  const remoteUtilityId = useRemoteUtilityId();
  const readingSource = "AX";
  return useSmartQuery(
    QueryKeyFactory.module.ax.schedule.getAllSOPs(
      remoteUtilityId,
      readingSource,
      searchData,
      sopAssociationType,
      operationType,
      page,
      limit
    ),
    () =>
      axScheduleApi.getAllSOPs(
        remoteUtilityId,
        readingSource,
        searchData,
        sopAssociationType,
        operationType,
        page,
        limit
      ),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useGetAllAssets = (
  page: number,
  limit: number,
  serviceAssociation?: string,
  searchData?: string,
  type?: string,
  area?: string,
  system?: string,
  network?: string,
  facility?: string
) => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.schedule.getAllAssets(
      remoteUtilityId,
      page,
      limit,
      searchData,
      type,
      area,
      system,
      network,
      facility
    ),
    () =>
      axScheduleApi.getAllAssets(
        remoteUtilityId,
        page,
        limit,
        searchData,
        type,
        area,
        system,
        network,
        facility
      ),
    {
      enabled: !!remoteUtilityId && serviceAssociation === "Asset",
    }
  );
};

export const useGetAllSystems = (
  serviceAssociation?: string,
  searchData?: string,
  type?: string,
  area?: string,
  facility?: string
) => {
  const remoteUtilityId = getRemoteUtilityId();
  const isPaginationRequired = 1;
  return useSmartQuery(
    QueryKeyFactory.module.ax.schedule.getAllSystems(
      remoteUtilityId,
      isPaginationRequired,
      searchData,
      type,
      area,
      facility
    ),
    () =>
      axScheduleApi.getAllSystems(
        remoteUtilityId,
        isPaginationRequired,
        searchData,
        type,
        area,
        facility
      ),
    {
      enabled: !!remoteUtilityId && serviceAssociation === "System",
    }
  );
};

export const useGetAllFacilities = (
  page: number,
  limit: number,
  serviceAssociation?: string,
  searchData?: string,
  type?: string,
  area?: string
) => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.schedule.getAllFacilities(
      remoteUtilityId,
      page,
      limit,
      searchData,
      type,
      area
    ),
    () =>
      axScheduleApi.getAllFacilities(
        remoteUtilityId,
        page,
        limit,
        searchData,
        type,
        area
      ),
    {
      enabled: !!remoteUtilityId && serviceAssociation === "Facility",
    }
  );
};

export const useGetAllNetworks = (
  page: number,
  limit: number,
  serviceAssociation?: string,
  searchData?: string,
  type?: string,
  facility?: string
) => {
  const remoteUtilityId = getRemoteUtilityId();
  const isPaginationRequired = 1;
  return useSmartQuery(
    QueryKeyFactory.module.ax.schedule.getAllNetworks(
      remoteUtilityId,
      page,
      limit,
      isPaginationRequired,
      searchData,
      type,
      facility
    ),
    () =>
      axScheduleApi.getAllNetworks(
        remoteUtilityId,
        page,
        limit,
        isPaginationRequired,
        searchData,
        type,
        facility
      ),
    {
      enabled: !!remoteUtilityId && serviceAssociation === "Network",
    }
  );
};

export const useCreateSchedule = (page: number, limit: number) => {
  const { toast } = useToast();
  const remoteUtilityId = getRemoteUtilityId();

  return useSmartMutation(
    (payload: any) => axScheduleApi.createSchedule(payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "Schedule Created",
          description: "Schedule has been created successfully.",
        });
        queryClient.refetchQueries({
          queryKey: [
            "ax",
            "schedule",
            "getAllSchedules",
            remoteUtilityId,
            page,
            limit,
          ],
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.schedule
            .getDashboardData(remoteUtilityId)
            .slice(0, 4),
        });
      },
      onError: (e) => {
        toast({
          title: "Error",
          description: e?.message || "Failed to create schedule",
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateSchedule = (page: number, limit: number) => {
  const { toast } = useToast();
  const remoteUtilityId = getRemoteUtilityId();

  return useSmartMutation(
    (payload: any) => axScheduleApi.updateSchedule(payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "Schedule Updated",
          description: "Schedule has been updated successfully.",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.schedule.getScheduleById(
            payload.id
          ),
        });
        queryClient.refetchQueries({
          queryKey: [
            "ax",
            "schedule",
            "getAllSchedules",
            remoteUtilityId,
            page,
            limit,
          ],
        });
        queryClient.removeQueries({
          queryKey: QueryKeyFactory.module.ax.shared.activityLogList(
            payload.id,
            "6"
          ),
        });
      },
      onError: (e) => {
        toast({
          title: "Error",
          description: e?.message || "Failed to update schedule",
          variant: "destructive",
        });
      },
    }
  );
};

export const useGetScheduleById = (id: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.schedule.getScheduleById(id),
    () => axScheduleApi.getScheduleById(id),
    {
      enabled: !!id,
    }
  );
};

export const useUpdateScheduleStatus = (page: number, limit: number) => {
  const remoteUtilityId = getRemoteUtilityId();
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => axScheduleApi.updateScheduleStatus(payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "Schedule Status Updated",
          description: "Schedule status has been updated successfully.",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.schedule.getScheduleById(
            payload.id
          ),
        });
        queryClient.refetchQueries({
          queryKey: [
            "ax",
            "schedule",
            "getAllSchedules",
            remoteUtilityId,
            page,
            limit,
          ],
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useUpdateSOManually = (page: number, limit: number) => {
  const remoteUtilityId = getRemoteUtilityId();
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => axScheduleApi.updateSOManually(payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "Service Order Triggered",
          description: "Service Order has been triggered successfully.",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.schedule.getScheduleById(
            payload.id
          ),
        });
        queryClient.refetchQueries({
          queryKey: [
            "ax",
            "schedule",
            "getAllSchedules",
            remoteUtilityId,
            page,
            limit,
          ],
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useGetAllScheduleAssets = (
  id: string,
  page: number,
  limit: number,
  search?: string
) => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.schedule.getAllScheduleAssets(
      id,
      page,
      limit,
      remoteUtilityId,
      search
    ),
    () =>
      axScheduleApi.getAllScheduleAssets(
        id,
        page,
        limit,
        remoteUtilityId,
        search
      ),
    {
      enabled: !!id && !!remoteUtilityId,
    }
  );
};

export const useGetAllScheduleServiceOrderHistory = (
  id: string,
  page: number,
  limit: number,
  search?: string,
  status?: number,
  dateFrom?: string,
  dateTo?: string
) => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.schedule.getAllScheduleServiceOrderHistory(
      id,
      page,
      limit,
      remoteUtilityId,
      search,
      status,
      dateFrom,
      dateTo
    ),
    () =>
      axScheduleApi.getAllScheduleServiceOrderHistory(
        id,
        page,
        limit,
        remoteUtilityId,
        search,
        status,
        dateFrom,
        dateTo
      ),
    {
      enabled: !!remoteUtilityId,
    }
  );
};
export const useGetSOPsForEventRules = (associationType?: string) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    [
      "ax",
      "schedule",
      "getSOPsForEventRules",
      remoteUtilityId,
      associationType,
    ],
    () => axScheduleApi.getSOPsForEventRules(remoteUtilityId, associationType),
    {
      enabled: !!remoteUtilityId,
    }
  );
};
